import {TextField} from '@mui/material';
import {DateRangePicker, LocalizationProvider} from '@mui/x-date-pickers-pro';
import {AdapterDayjs} from '@mui/x-date-pickers-pro/AdapterDayjs';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Tooltip as ReactTooltip} from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import SessionFilter from '../Components/Global_Components/common/SessionFilter';
import BasicTable from '../Components/Global_Components/reacttable/table';
import {fetchPatientBill, getArchiveData} from '../Redux/Thunk/Actions';
import '../styles/Patients.scss';
import {downloadExcel, printPDF} from '../utils/printUtils';

dayjs.extend(localizedFormat);

const BillReportDetails = ({loaderchange, popupalert, popuptext}) => {
  const dispatch = useDispatch();
  const userdata = useSelector(state => state.counter.userdata);
  const sideactive = useSelector(state => state.counter.sidebarnav);

  // State management
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState({pageIndex: 1, pageSize: 10});
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState([dayjs(), dayjs()]);
  const [showArchive, setShowArchive] = useState(false);
  const [totalBill, setTotalBill] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [printableData, setPrintableData] = useState({});
  const [datacount, setdatacount] = useState(10);
  const [session, setSession] = useState('');

  // Derived values
  const startDate = dayjs(dateRange[0]).format('YYYY-MM-DD');
  const endDate = dayjs(dateRange[1]).format('YYYY-MM-DD');
  const isAdmin = userdata.role === 'Admin';

  const headers = {
    serialNo: 'S.NO',
    date: 'DATE',
    patientName: 'PATIENTS  NAME',
    billAmount: 'BILL AMOUNT',
    cashCollected: 'CASH COLLECTED',
    balance: 'BALANCE',
    card: 'CARD',
    upi: 'UPI',
    remarks: 'REMARKS'
  };

  const subHeaders = {
    billTotal: 'BILL',
    cashTotal: 'CASH',
    totalBalance: 'BALANCE',
    cardTotal: 'CARD',
    upiTotal: 'UPI'
  };

  const fetchData = async (isArchive = false) => {
    const payload = {
      page: pagination.pageIndex,
      page_size: pagination.pageSize,
      search: searchQuery,
      from_date: startDate,
      to_date: endDate,
      range: true,
      session: session
    };

    loaderchange(true);

    const action = isArchive ? getArchiveData : fetchPatientBill;
    const response = await dispatch(action(payload));

    if (response?.payload?.status?.code === 200) {
      const {data, total_count, total_bill_price} = response.payload;
      setdatacount(total_count);
      setTableData(data.data || data);
      setTotalBill(total_bill_price || 0);

      if (isInitialLoad) setIsInitialLoad(false);
    } else {
      popupalert(true);
      popuptext('No Data Available');
      setTimeout(() => popupalert(false), 2000);
    }
    loaderchange(false);
  };

  useEffect(() => {
    fetchData(showArchive);
  }, [pagination, searchQuery, startDate, endDate, showArchive, session]);

  const [billTotal, cashTotal, cardTotal, upiTotal, totalBalance] = useMemo(() => {
    let bill = 0,
      cash = 0,
      card = 0,
      upi = 0,
      balance = 0;
    tableData.forEach(item => {
      bill += Math.round(Number(item.bill_price) || 0);
      cash += Math.round(Number(item.amount_one) || 0);
      card += Math.round(Number(item.amount_two) || 0);
      upi += Math.round(Number(item.amount_three) || 0);
    });
    balance = Math.round(cash ?? 0) + Math.round(card ?? 0) + Math.round(upi ?? 0) - Math.round(bill ?? 0);
    return [bill, cash, card, upi, balance];
  }, [tableData]);

  const [currentPageBillTotal, currentPageCashTotal, currentPageCardTotal, currentPageUpiTotal, currentPageBalance] = useMemo(() => {
    let bill = 0,
      cash = 0,
      card = 0,
      upi = 0,
      balance = 0;
    tableData.forEach(item => {
      bill += Math.round(Number(item.bill_price) || 0);
      cash += Math.round(Number(item.amount_one) || 0);
      card += Math.round(Number(item.amount_two) || 0);
      upi += Math.round(Number(item.amount_three) || 0);
    });
    balance = Math.round(cash ?? 0) + Math.round(card ?? 0) + Math.round(upi ?? 0) - Math.round(bill ?? 0);
    return [bill, cash, card, upi, balance];
  }, [tableData]);

  const handlePageSizeChange = useCallback(pageSize => {
    setPagination(prev => {
      if (prev.pageSize === pageSize) return prev;
      return {...prev, pageSize, pageIndex: 1};
    });
  }, []);

  const handlePageIndexChange = useCallback(pageIndex => {
    setPagination(prev => {
      if (prev.pageIndex === pageIndex) return prev;
      return {...prev, pageIndex};
    });
  }, []);

  const handleDateChange = newDates => {
    setDateRange(newDates);
    if (newDates[0] && newDates[1]) {
      fetchData();
    }
  };

  const handleSessionChange = useCallback(newSession => {
    setSession(newSession);
    setPagination(prev => ({...prev, pageIndex: 1}));
  }, []);

  const handleHTMLData = value => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(value, 'text/html');

    let remarks = [
      ...new Set(
        Array.from(doc.querySelectorAll('.rs-picker-toggle-value, .createTime.show'))
          .map(el => el.textContent?.trim())
          .filter(text => {
            const isNumeric = !isNaN(text) && !isNaN(parseFloat(text));
            return text && !isNumeric && text !== 'Select Group';
          })
      )
    ];
    remarks = remarks.length > 0 ? remarks.join(', ') : '--';
    return remarks;
  };

  const formattedData = tableData.map((item, index) => ({
    serialNo: index + 1,
    date: dayjs(item.created_at).format('DD-MM-YYYY'),
    patientName: `${item.patient_id?.titles || ''} ${item.patient_id?.first_name || ''} ${item.patient_id?.last_name || ''}`.trim(),
    billAmount: Math.round(item.bill_price) || 0,
    cashCollected: Math.round(item.amount_one) || 0,
    balance: Math.round(item.amount_one ?? 0) + Math.round(item.amount_two ?? 0) + Math.round(item.amount_three ?? 0) - Math.round(item.bill_price ?? 0),
    card: Math.round(item.amount_two) || 0,
    upi: Math.round(item.amount_three) || 0,
    remarks: handleHTMLData(item.report_template)
  }));

  const handlePrintPdf = () => {
    const subRow = [billTotal, cashTotal, totalBalance, cardTotal, upiTotal];
    printPDF({title: 'Billing Report', headers, data: formattedData, subHeaders, subRow});
  };
  const handleDownload = () => {
    const subRow = ['', 'TOTAL', '', billTotal, cashTotal, totalBalance, cardTotal, upiTotal, ''];
    downloadExcel({title: 'Billing Report', headers, data: formattedData, subRow});
  };

  const tableColumns = useMemo(
    () => [
      {
        Header: 'SI No.',
        accessor: '',
        Cell: row => {
          return (
            <>
              <span>
                <div style={{marginTop: '3px'}} data-tooltip-id='my-tooltip' data-tooltip-content={String(1 + Number(row.row.id))}>
                  {1 + Number(row.row.id)}.
                </div>
              </span>
            </>
          );
        }
      },
      {
        Header: 'Date',
        accessor: 'created_at',
        Cell: ({value}) => <TooltipContent>{dayjs(value).format('DD-MM-YYYY')}</TooltipContent>,
        Footer: () => <span>TOTAL</span>
      },
      {
        Header: 'Patient Name',
        accessor: 'patient_id',
        Cell: ({row}) => (
          <TooltipContent>
            {row.original.patient_id?.titles}.{row.original.patient_id?.first_name} {row.original.patient_id?.last_name}
          </TooltipContent>
        )
      },
      {
        Header: 'Bill Amount',
        accessor: 'bill_price',
        Cell: ({value}) => <TooltipContent>{Math.round(value) || '--'}</TooltipContent>,
        Footer: () => <span>{currentPageBillTotal && Math.round(currentPageBillTotal)}</span>
      },
      {
        Header: 'Cash Collected',
        accessor: 'amount_one',
        Cell: ({value}) => <TooltipContent>{Math.round(value) || '--'}</TooltipContent>,
        Footer: () => <span>{currentPageCashTotal && Math.round(currentPageCashTotal)}</span>
      },
      {
        Header: 'Balance',
        accessor: 'balance',
        Cell: ({row}) => {
          const bill = row.original.bill_price;
          const cash = row.original.amount_one;
          const card = row.original.amount_two;
          const upi = row.original.amount_three;
          const balance = Math.round(cash ?? 0) + Math.round(card ?? 0) + Math.round(upi ?? 0) - Math.round(bill ?? 0);

          return <TooltipContent>{!isNaN(balance) ? balance : '--'}</TooltipContent>;
        },
        Footer: () => <span>{currentPageBalance && Math.round(currentPageBalance)}</span>
      },
      {
        Header: 'Card',
        accessor: 'amount_two',
        Cell: ({value}) => <TooltipContent>{Math.round(value) || '--'}</TooltipContent>,
        Footer: () => <span>{currentPageCardTotal && Math.round(currentPageCardTotal)}</span>
      },
      {
        Header: 'UPI',
        accessor: 'amount_three',
        Cell: ({value}) => <TooltipContent>{Math.round(value) || '--'}</TooltipContent>,
        Footer: () => <span>{currentPageUpiTotal && Math.round(currentPageUpiTotal)}</span>
      },
      {
        Header: 'Remarks',
        accessor: 'report_template',
        Cell: ({value}) => {
          if (!value) return <TooltipContent>--</TooltipContent>;

          const remarks = handleHTMLData(value);

          return (
            <div
              style={{
                maxWidth: '300px',
                overflowWrap: 'break-word',
                whiteSpace: 'normal',
                maxHeight: 'auto',
                overflow: 'auto'
              }}>
              {remarks}
            </div>
          );
          // return <TooltipContent>{remarks.length > 0 ? remarks.join(', ') : '--'}</TooltipContent>;
        }
      }
    ],
    [showArchive, currentPageBillTotal, currentPageCashTotal, currentPageBalance, currentPageCardTotal, currentPageUpiTotal]
  );

  return (
    <div className={`maincontainer-patient ${sideactive ? 'maincontainer-patient-sa' : ''}`}>
      <div className='patient-header'>
        <Breadcrumbs />
        <div className='add_btn' style={{display: 'flex', gap: '10px'}}>
          <button onClick={handlePrintPdf} className='btn_1'>
            Print
          </button>
          <button onClick={handleDownload} className='btn_1'>
            Download
          </button>
        </div>
      </div>

      {isAdmin && (
        <div className='billableCard'>
          <p>
            {startDate === dayjs().format('YYYY-MM-DD') && endDate === dayjs().format('YYYY-MM-DD') ? "Today's Billing Overview" : 'Billing Overview'}
            &nbsp;:&nbsp;
            <span>{totalBill ?? 0}&nbsp;Rs</span>
          </p>
        </div>
      )}

      <div className='card'>
        <div className='card-body'>
          <div className='table-controls' style={{display: 'flex', justifyContent: 'space-between'}}>
            <SearchInput value={searchQuery} onChange={setSearchQuery} />
            <div style={{display: 'flex', gap: '10px'}}>
              <SessionFilter value={session} onChange={handleSessionChange} />
              <DateRangeFilter value={dateRange} onChange={handleDateChange} />
            </div>
          </div>

          <BasicTable
            columns={tableColumns}
            data={tableData}
            pagesize={handlePageSizeChange}
            pageindex={handlePageIndexChange}
            dtpagesize={pagination.pageSize}
            dtpageindex={pagination.pageIndex}
            datacount={datacount}
            showAll={true}
          />
        </div>
      </div>

      <ReactTooltip id='table-tooltip' arrowColor='transparent' place='bottom-start' delayShow={200} className='custom-tooltip' />
    </div>
  );
};

// Sub-components for better readability
const TooltipContent = ({children}) => (
  <div data-tooltip-id='table-tooltip' data-tooltip-content={children}>
    {children}
  </div>
);

const Breadcrumbs = () => (
  <div className='row'>
    <div className='col'>
      <h3 className='page-title'>Billing Report</h3>
      <ul className='breadcrumb'>
        <li className='breadcrumb-item'>
          <a href='/'>Dashboard</a>
        </li>
        <li className='breadcrumb-item active'>Billing Report</li>
      </ul>
    </div>
  </div>
);

const SearchInput = ({value, onChange}) => (
  <div className='input-group rounded' style={{width: '250px', height: '40px'}}>
    <input
      type='search'
      class='form-control rounded'
      aria-label='Search'
      aria-describedby='search-addon'
      value={value}
      style={{
        height: '40px'
      }}
      onChange={e => onChange(e.target.value)}
      placeholder='Search...'
      maxLength={100}
    />
    <span class='input-group-text border-0 bg-transparent p-0' id='search-addon'>
      <i
        class='bi bi-search'
        style={{
          color: '#1b5a90',
          width: '20px',
          height: '20px',
          marginLeft: '10px'
        }}></i>
    </span>
  </div>
);

const DateRangeFilter = ({value, onChange}) => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DateRangePicker
      value={value}
      onChange={onChange}
      maxDate={dayjs()}
      renderInput={(startProps, endProps) => (
        <>
          <FormattedDateField {...startProps} />
          <FormattedDateField {...endProps} />
        </>
      )}
    />
  </LocalizationProvider>
);

const FormattedDateField = props => <TextField {...props} InputLabelProps={{shrink: true}} inputProps={{...props.inputProps, value: dayjs(props.inputProps.value).format('DD-MM-YYYY')}} />;

export default BillReportDetails;
